/**
 * ★ Fase 94 (Doc 02) — Sezioni riutilizzabili per le landing IT (Three Garden, Narea…):
 *   • ZoomAgendaSection      → "Agenda della Zoom (30 min)" + slot mini-video (solo se configurato)
 *   • ProofsSection          → "Prove verificabili" (brochure, testimonianze, date Pass live, confronto CEO)
 *   • VerificationPassBridge → bridge esplicito Zoom → Verification Pass (date live dal CRM, CTA con UTM)
 *   • useVpDates(source)     → date Verification Pass live da GET /api/public/funnel/<source>
 *
 * Regola Doc 02: nessun dato inventato. Prezzi/date arrivano da config landing o dal CRM;
 * dove manca un asset (es. video 45s) il blocco NON viene renderizzato e resta un TODO esplicito.
 * Palette via `tokens` (la landing mantiene i propri colori).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Play, CalendarClock, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { trackFunnelEvent, withUtm } from '../../lib/attribution';
import { NoObligationNote } from './NoObligationNote';
import { youtubeEmbed, youtubeThumb } from '../../lib/testimonials';

const DEFAULT_TOKENS = {
  bg: '#F9F6F0', bg2: '#F1ECE2', paper: '#FFFDF9', ink: '#1A2318', inkSoft: '#59615B',
  accent: '#3C5330', accentDeep: '#2F4226', line: '#E3DCCF', soft: '#EBEEE3', onAccent: '#FFFBF3',
};

/** Date Verification Pass live dal CRM (es. source='it-al' → IT-AL). */
export function useVpDates(source) {
  const [dates, setDates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!source) { setLoaded(true); return undefined; }
    let alive = true;
    api.get(`/public/funnel/${source}`)
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data?.vp_dates) ? r.data.vp_dates : [];
        const today = new Date().toISOString().slice(0, 10);
        setDates(list
          .filter((d) => d && d.label && (!d.start || d.start >= today))
          .map((d) => ({ id: d.id || d.label, label: d.label, start: d.start, left: d.left ?? null, total: d.total ?? null })));
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [source]);
  return { dates, loaded };
}

function Kicker({ children, tokens }) {
  return <div className="text-[11px] font-sans font-semibold tracking-[0.28em] uppercase" style={{ color: tokens.accent }}>{children}</div>;
}

function Title({ children, tokens, testId, light = false, className = '' }) {
  return (
    <h2 className={`font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.08] tracking-tight ${className}`} style={{ color: light ? tokens.onAccent : tokens.ink }} data-testid={testId}>
      {children}
    </h2>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/** Agenda della Zoom (30 min). `video` = { id, title, kicker, text } → renderizzato SOLO se `video.id` è valorizzato. */
export function ZoomAgendaSection({ c, tokens: tk, video, onCta, ctaLabel, testIdPrefix = 'agenda', id = 'agenda', lang = 'it' }) {
  const tokens = { ...DEFAULT_TOKENS, ...(tk || {}) };
  const [playing, setPlaying] = useState(false);
  if (!c) return null;
  const hasVideo = !!(video && video.id);
  const play = () => {
    setPlaying(true);
    trackFunnelEvent('video_play', { funnel: c.funnel, label: video.title || 'mini-video', section: id });
  };
  return (
    <section id={id} className="relative scroll-mt-16" style={{ background: tokens.bg }} data-testid={`${testIdPrefix}-section`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className={`grid grid-cols-1 ${hasVideo ? 'lg:grid-cols-[1fr_minmax(0,440px)]' : 'lg:grid-cols-[minmax(0,380px)_1fr]'} gap-10 lg:gap-14 items-start`}>
          <div>
            <Kicker tokens={tokens}>{c.kicker}</Kicker>
            <Title className="mt-3" tokens={tokens} testId={`${testIdPrefix}-title`}>{c.title}</Title>
            <p className="mt-4 text-[15px] sm:text-[16px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }} data-testid={`${testIdPrefix}-subtitle`}>{c.subtitle}</p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12px] font-sans font-semibold" style={{ background: tokens.soft, color: tokens.ink }} data-testid={`${testIdPrefix}-meta`}>
              <Clock className="w-4 h-4" style={{ color: tokens.accent }} aria-hidden="true" /> {c.meta}
            </div>
            {onCta && (
              <div className="mt-6">
                <button type="button" onClick={() => onCta(ctaLabel)} className="inline-flex items-center gap-2 rounded-xl px-6 min-h-12 py-3 text-left text-[13px] sm:text-sm font-sans font-semibold transition-transform duration-200 hover:-translate-y-px" style={{ background: tokens.accent, color: tokens.onAccent }} data-testid={`${testIdPrefix}-cta`}>
                  {ctaLabel} <ArrowRight className="w-4 h-4 shrink-0" />
                </button>
              </div>
            )}
          </div>

          {hasVideo ? (
            <figure className="rounded-2xl overflow-hidden border" style={{ borderColor: tokens.line, background: tokens.paper }} data-testid={`${testIdPrefix}-video`}>
              <div className="relative aspect-video" style={{ background: '#0E1512' }}>
                {playing ? (
                  <iframe
                    src={youtubeEmbed(video.id, { autoplay: true })}
                    title={video.title}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <button type="button" onClick={play} className="absolute inset-0 w-full h-full group" aria-label={video.title} data-testid={`${testIdPrefix}-video-play`}>
                    <img src={youtubeThumb(video.id)} alt="" className="w-full h-full object-cover opacity-90" loading="lazy" />
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="w-16 h-16 rounded-full grid place-items-center transition-transform duration-200 group-hover:scale-105" style={{ background: tokens.accent, color: tokens.onAccent }}>
                        <Play className="w-6 h-6 translate-x-0.5" fill="currentColor" />
                      </span>
                    </span>
                  </button>
                )}
              </div>
              <figcaption className="p-4">
                <div className="text-[11px] font-sans font-bold tracking-[0.2em] uppercase" style={{ color: tokens.accent }}>{video.kicker}</div>
                <p className="mt-1 text-[13.5px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }}>{video.text}</p>
              </figcaption>
            </figure>
          ) : null}

          <ol className={`${hasVideo ? 'lg:col-span-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}`} data-testid={`${testIdPrefix}-items`}>
            {c.items.map((it, i) => (
              <li key={it.t} className="rounded-2xl border p-5 sm:p-6 flex flex-col" style={{ background: tokens.paper, borderColor: tokens.line }} data-testid={`${testIdPrefix}-item-${i + 1}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-serif text-[15px]" style={{ color: tokens.accent }}>{String(i + 1).padStart(2, '0')}</span>
                  <span className="rounded-full px-2.5 py-1 text-[11px] font-sans font-semibold tracking-[0.08em]" style={{ background: tokens.soft, color: tokens.ink }}>{it.time}</span>
                </div>
                <h3 className="mt-3 font-serif text-[18px] leading-tight" style={{ color: tokens.ink }}>{it.t}</h3>
                <p className="mt-2 text-[13.5px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }}>{it.d}</p>
              </li>
            ))}
          </ol>
        </div>
        {c.note && (
          <p className="mt-6 text-[13px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }} data-testid={`${testIdPrefix}-note`}>{c.note}</p>
        )}
        <NoObligationNote lang={lang} theme="light" className="mt-3 !justify-start" testId={`${testIdPrefix}-no-obligation`} compact />
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/** Prove verificabili. `items[]` = { icon, t, d, meta?, action?: { label, onClick|href } , dates?: true } */
export function ProofsSection({ c, tokens: tk, vpDates = [], testIdPrefix = 'proofs', id = 'prove' }) {
  const tokens = { ...DEFAULT_TOKENS, ...(tk || {}) };
  if (!c) return null;
  return (
    <section id={id} className="relative scroll-mt-16" style={{ background: tokens.bg2 }} data-testid={`${testIdPrefix}-section`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-3xl">
          <Kicker tokens={tokens}>{c.kicker}</Kicker>
          <Title className="mt-3" tokens={tokens} testId={`${testIdPrefix}-title`}>{c.title}</Title>
          <p className="mt-4 text-[15px] sm:text-[16px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }}>{c.intro}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {c.items.map((p, i) => {
            const Icon = p.icon;
            return (
              <article key={p.t} className="rounded-2xl border p-5 flex flex-col" style={{ background: tokens.paper, borderColor: tokens.line }} data-testid={`${testIdPrefix}-item-${i + 1}`}>
                <Icon className="w-8 h-8" style={{ color: tokens.accent }} strokeWidth={1.4} aria-hidden="true" />
                <h3 className="mt-4 font-serif text-[18px] leading-tight" style={{ color: tokens.ink }}>{p.t}</h3>
                <p className="mt-2 text-[13px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }}>{p.d}</p>
                {p.dates && (
                  <ul className="mt-3 space-y-1.5" data-testid={`${testIdPrefix}-dates`}>
                    {vpDates.length === 0 && (
                      <li className="text-[12.5px] font-sans" style={{ color: tokens.inkSoft }} data-testid={`${testIdPrefix}-dates-empty`}>{c.datesEmpty}</li>
                    )}
                    {vpDates.slice(0, 3).map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-2 text-[12.5px] font-sans rounded-lg px-2.5 py-1.5" style={{ background: tokens.soft, color: tokens.ink }}>
                        <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" style={{ color: tokens.accent }} aria-hidden="true" /> {d.label}</span>
                        {typeof d.left === 'number' && (
                          <span className="inline-flex items-center gap-1 font-semibold" style={{ color: d.left > 0 ? tokens.accent : '#B4533F' }}>
                            <Users className="w-3.5 h-3.5" aria-hidden="true" /> {d.left > 0 ? `${d.left} ${c.seatsLabel}` : c.soldOut}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {p.meta && <div className="mt-auto pt-4 text-[11px] font-sans font-semibold tracking-[0.12em] uppercase" style={{ color: tokens.accent }}>{p.meta}</div>}
                {p.action && (
                  p.action.href ? (
                    <Link to={p.action.href} className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-sans font-semibold underline underline-offset-4" style={{ color: tokens.ink }} data-testid={`${testIdPrefix}-item-${i + 1}-action`}>
                      {p.action.label} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  ) : (
                    <button type="button" onClick={p.action.onClick} className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-sans font-semibold underline underline-offset-4 text-left" style={{ color: tokens.ink }} data-testid={`${testIdPrefix}-item-${i + 1}-action`}>
                      {p.action.label} <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )
                )}
              </article>
            );
          })}
        </div>
        {c.footnote && <p className="mt-6 text-[12.5px] font-sans leading-relaxed" style={{ color: tokens.inkSoft }} data-testid={`${testIdPrefix}-footnote`}>{c.footnote}</p>}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/** Bridge esplicito Zoom → Verification Pass. `c.href` = destinazione configurabile (hub #verification). */
export function VerificationPassBridge({ c, tokens: tk, vpDates = [], onPrimary, primaryLabel, funnel, testIdPrefix = 'bridge', id = 'percorso', lang = 'it', image }) {
  const tokens = { ...DEFAULT_TOKENS, ...(tk || {}) };
  if (!c) return null;
  const href = withUtm(c.href);
  const onBridgeClick = () => trackFunnelEvent('cta_click', { funnel, label: 'verification_pass_bridge', target: c.href });
  return (
    <section id={id} className="relative scroll-mt-16 overflow-hidden" style={{ background: tokens.accentDeep }} data-testid={`${testIdPrefix}-section`}>
      {image && (
        <div className="absolute inset-0" aria-hidden="true">
          <img src={image.src} alt="" className="w-full h-full object-cover" style={{ objectPosition: image.pos || 'center' }} loading="lazy" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${tokens.accentDeep} 0%, ${tokens.accentDeep}F2 45%, ${tokens.accentDeep}B8 100%)` }} />
        </div>
      )}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,460px)_1fr] gap-10 lg:gap-14 items-start">
          <div>
            <div className="text-[11px] font-sans font-semibold tracking-[0.28em] uppercase" style={{ color: '#D8E0CF' }}>{c.kicker}</div>
            <Title className="mt-3" tokens={tokens} light testId={`${testIdPrefix}-title`}>{c.title[0]}<br />{c.title[1]}</Title>
            <p className="mt-4 text-[15px] sm:text-[16px] font-sans leading-relaxed" style={{ color: '#E3E8DC' }} data-testid={`${testIdPrefix}-text`}>{c.text}</p>

            <div className="mt-6 rounded-2xl border p-5" style={{ borderColor: 'rgba(255,255,255,0.22)', background: 'rgba(255,251,243,0.08)', backdropFilter: 'blur(12px)' }} data-testid={`${testIdPrefix}-dates-card`}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-serif text-[17px]" style={{ color: tokens.onAccent }}>{c.datesTitle}</div>
                <span className="rounded-full px-2.5 py-1 text-[11px] font-sans font-bold tracking-[0.08em]" style={{ background: tokens.onAccent, color: tokens.accentDeep }} data-testid={`${testIdPrefix}-price`}>{c.price}</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {vpDates.length === 0 && <li className="text-[13px] font-sans" style={{ color: '#D8E0CF' }} data-testid={`${testIdPrefix}-dates-empty`}>{c.datesEmpty}</li>}
                {vpDates.slice(0, 4).map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 text-[13px] font-sans" style={{ color: tokens.onAccent }} data-testid={`${testIdPrefix}-date`}>
                    <span className="inline-flex items-center gap-2"><CalendarClock className="w-4 h-4" style={{ color: '#D8E0CF' }} aria-hidden="true" /> {d.label}</span>
                    {typeof d.left === 'number' && <span className="text-[12px]" style={{ color: d.left > 0 ? '#D8E0CF' : '#F2C4B8' }}>{d.left > 0 ? `${d.left} ${c.seatsLabel}` : c.soldOut}</span>}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[11.5px] font-sans leading-relaxed" style={{ color: '#C9D2C0' }}>{c.datesNote}</p>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {onPrimary && (
                <button type="button" onClick={() => onPrimary(primaryLabel)} className="inline-flex items-center justify-center gap-2 rounded-xl px-6 min-h-12 py-3 text-[13px] sm:text-sm font-sans font-semibold whitespace-nowrap transition-transform duration-200 hover:-translate-y-px" style={{ background: tokens.onAccent, color: tokens.accentDeep }} data-testid={`${testIdPrefix}-primary-cta`}>
                  {primaryLabel} <ArrowRight className="w-4 h-4 shrink-0" />
                </button>
              )}
              <Link to={href} onClick={onBridgeClick} className="inline-flex items-center justify-center gap-2 rounded-xl px-6 min-h-12 py-3 text-[13px] sm:text-sm font-sans font-semibold border whitespace-nowrap transition-colors duration-200 hover:bg-white/10" style={{ borderColor: 'rgba(255,255,255,0.45)', color: tokens.onAccent }} data-testid={`${testIdPrefix}-cta`}>
                {c.cta} <ArrowRight className="w-4 h-4 shrink-0" />
              </Link>
            </div>
            <NoObligationNote lang={lang} theme="dark" color="#D8E0CF" iconColor={tokens.onAccent} className="mt-4 !justify-start" testId={`${testIdPrefix}-no-obligation`} compact />
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`${testIdPrefix}-steps`}>
            {c.steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <li key={s.t} className="rounded-2xl border p-5 sm:p-6 flex flex-col" style={{ borderColor: 'rgba(255,255,255,0.22)', background: 'rgba(255,251,243,0.08)', backdropFilter: 'blur(12px)' }} data-testid={`${testIdPrefix}-step-${i + 1}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-[15px]" style={{ color: '#D8E0CF' }}>{String(i + 1).padStart(2, '0')}</span>
                    <span className="w-10 h-10 rounded-full border grid place-items-center" style={{ borderColor: 'rgba(255,255,255,0.4)' }}>
                      <Icon className="w-5 h-5" style={{ color: tokens.onAccent }} strokeWidth={1.6} aria-hidden="true" />
                    </span>
                  </div>
                  <h3 className="mt-4 font-serif text-[18px] leading-tight" style={{ color: tokens.onAccent }}>{s.t}</h3>
                  <p className="mt-2 text-[13.5px] font-sans leading-relaxed" style={{ color: '#D8E0CF' }}>{s.d}</p>
                  {s.tag && <span className="mt-auto pt-4 text-[11px] font-sans font-semibold tracking-[0.12em] uppercase" style={{ color: '#D8E0CF' }}>{s.tag}</span>}
                </li>
              );
            })}
            <li className="md:col-span-3 rounded-2xl border p-5 sm:p-6" style={{ borderColor: 'rgba(255,255,255,0.22)', background: 'rgba(255,251,243,0.08)', backdropFilter: 'blur(12px)' }} data-testid={`${testIdPrefix}-includes`}>
              <div className="font-serif text-[17px]" style={{ color: tokens.onAccent }}>{c.includesTitle}</div>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {c.includes.map((x) => (
                  <li key={x} className="flex items-start gap-2 text-[13.5px] font-sans leading-relaxed" style={{ color: '#E3E8DC' }}>
                    <ArrowRight className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#D8E0CF' }} aria-hidden="true" /> {x}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[12px] font-sans leading-relaxed" style={{ color: '#C9D2C0' }}>{c.includesNote}</p>
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
}
